import routeDirectory from '~/navigation/routes'

import type { BillingCompletenessItem, BillingCompletenessStatus } from './types'

/**
 * Checklist vocabulary shared with the backend's `get_billing_setup_overview` (`completeness.items[].key`).
 * `products.pricing` and `billing.stripeConnect` are client-only extras; everything else is emitted by the backend.
 */
export type BillingCompletenessKey =
  | 'products.active'
  | 'products.pricing'
  | 'products.entitySelections'
  | 'products.requiredMetadata'
  | 'agreements.active'
  | 'agreements.products'
  | 'tax.origin'
  | 'tax.defaultBehavior'
  | 'tax.defaultCode'
  | 'tax.rates'
  | 'tax.registrations'
  | 'billing.stripeConnect'

/** Dots would nest the locale tree, so each key maps to a flat token used in `completeness_<token>_<status>`. */
export const COMPLETENESS_TOKENS = {
  'products.active': 'productsActive',
  'products.pricing': 'productsPricing',
  'products.entitySelections': 'productsEntitySelections',
  'products.requiredMetadata': 'productsRequiredMetadata',
  'agreements.active': 'agreementsActive',
  'agreements.products': 'agreementsProducts',
  'tax.origin': 'taxOrigin',
  'tax.defaultBehavior': 'taxDefaultBehavior',
  'tax.defaultCode': 'taxDefaultCode',
  'tax.rates': 'taxRates',
  'tax.registrations': 'taxRegistrations',
  'billing.stripeConnect': 'stripeConnect'
} as const satisfies Record<BillingCompletenessKey, string>

export type CompletenessToken = (typeof COMPLETENESS_TOKENS)[BillingCompletenessKey]
export type CompletenessMessageKey = `completeness_${CompletenessToken}_${BillingCompletenessStatus}`

const TAX_SETTINGS_TAB = `${routeDirectory.settings.taxes.root}?tab=settings`

/** Where to go to fix each item when the checklist has no more specific target (e.g. a particular product). */
const COMPLETENESS_HREFS: Readonly<Record<BillingCompletenessKey, string>> = {
  'products.active': routeDirectory.settings.products.new,
  'products.pricing': routeDirectory.settings.products.root,
  'products.entitySelections': routeDirectory.settings.products.root,
  'products.requiredMetadata': routeDirectory.settings.products.root,
  'agreements.active': routeDirectory.settings.agreements.new,
  'agreements.products': routeDirectory.settings.agreements.root,
  'tax.origin': TAX_SETTINGS_TAB,
  'tax.defaultBehavior': TAX_SETTINGS_TAB,
  'tax.defaultCode': TAX_SETTINGS_TAB,
  'tax.rates': routeDirectory.settings.taxes.rates,
  'tax.registrations': TAX_SETTINGS_TAB,
  'billing.stripeConnect': routeDirectory.settings.billingHub
}

export function isBillingCompletenessKey(key: string): key is BillingCompletenessKey {
  return key in COMPLETENESS_TOKENS
}

export function completenessMessageKey(key: BillingCompletenessKey, status: BillingCompletenessStatus): CompletenessMessageKey {
  return `completeness_${COMPLETENESS_TOKENS[key]}_${status}`
}

export function completenessHref(key: BillingCompletenessKey): string {
  return COMPLETENESS_HREFS[key]
}

export type ResolvedCompletenessItem = {
  key: string
  status: BillingCompletenessStatus
  message: string
  /** The backend's own wording when it adds specifics beyond the localized label. */
  detail?: string
  href?: string
}

/**
 * Turns backend checklist items into card rows: known keys get the localized label and a fix link (keeping the
 * backend's wording as a detail line when it says more), unknown keys fall back to that wording with no link.
 */
export function resolveCompletenessItems(
  items: BillingCompletenessItem[],
  t: (key: CompletenessMessageKey) => string
): ResolvedCompletenessItem[] {
  return items.map((item) => {
    const backendMessage = item.message.trim()
    if (!isBillingCompletenessKey(item.key)) {
      return { key: item.key, status: item.status, message: backendMessage || item.key }
    }
    const message = t(completenessMessageKey(item.key, item.status))
    return {
      key: item.key,
      status: item.status,
      message,
      detail: backendMessage && backendMessage !== message ? backendMessage : undefined,
      href: completenessHref(item.key)
    }
  })
}
